/**
 * Audit page — Module 4 "Prospecteur IA".
 *
 * Three input modes:
 *   • Lien     — paste an Instagram / TikTok / LinkedIn profile URL; the API
 *                captures it headlessly. On capture failure → manual fallback.
 *   • Capture  — drag-drop a screenshot of the profile.
 *   • Texte    — paste the bio + posts.
 *
 * Inea runs a full strategic analysis; the prospect is auto-saved to the CRM.
 */
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Loader2,
  UserSearch,
  ImagePlus,
  X,
  Sparkles,
  ArrowRight,
  ClipboardList,
  Link2,
  Type,
  Camera,
  AlertCircle,
} from 'lucide-react';
import { useAudit, type AuditedProspect } from '@/hooks/useProspect';
import { ApiError, isQuotaError } from '@/lib/api';
import { QuotaReachedNotice } from '@/components/QuotaReachedNotice';
import { AuditReport } from '@/components/AuditReport';
import { useCelebrate } from '@/hooks/useCelebrate';

const ACCEPTED = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const MAX_BYTES = 6 * 1024 * 1024; // 6 MB — keeps the base64 payload under the API limit.

type Mode = 'url' | 'screenshot' | 'text';

export default function AuditPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const audit = useAudit();
  const celebrate = useCelebrate();
  const MODES: { id: Mode; labelKey: string; icon: typeof Link2 }[] = [
    { id: 'url', labelKey: 'audit.tabUrl', icon: Link2 },
    { id: 'screenshot', labelKey: 'audit.tabScreenshot', icon: Camera },
    { id: 'text', labelKey: 'audit.tabText', icon: Type },
  ];
  const [mode, setMode] = useState<Mode>('url');
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const [image, setImage] = useState<{ base64: string; mediaType: string; preview: string } | null>(
    null,
  );
  const [fileError, setFileError] = useState<string | null>(null);
  const [result, setResult] = useState<AuditedProspect | null>(null);
  /** Set when a URL capture fails — drives the manual-fallback banner. */
  const [captureFailed, setCaptureFailed] = useState<string | null>(null);

  const handleFile = useCallback((file: File) => {
    setFileError(null);
    if (!ACCEPTED.includes(file.type)) {
      setFileError(t('audit.fileFormat'));
      return;
    }
    if (file.size > MAX_BYTES) {
      setFileError(t('audit.fileTooBig'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.split(',')[1] ?? '';
      setImage({ base64, mediaType: file.type, preview: dataUrl });
    };
    reader.readAsDataURL(file);
  }, []);

  const isUrlValid =
    /^https?:\/\/(www\.|[a-z]{2}\.|m\.)?(instagram\.com|tiktok\.com|linkedin\.com|facebook\.com|fb\.com)\//i.test(
      url.trim(),
    );
  const canSubmit =
    !audit.isPending &&
    ((mode === 'url' && isUrlValid) ||
      (mode === 'screenshot' && !!image) ||
      (mode === 'text' && text.trim().length > 0));

  function runAudit(): void {
    if (!canSubmit) return;
    setCaptureFailed(null);
    const input =
      mode === 'url'
        ? { url: url.trim() }
        : mode === 'screenshot'
          ? { screenshot: image ? { base64: image.base64, mediaType: image.mediaType } : undefined }
          : { text: text.trim() };
    audit.mutate(input, {
      onSuccess: (p) => {
        setResult(p);
        celebrate('first-audit');
      },
      onError: (err) => {
        // A capture failure (anti-bot, private profile) → guide to manual mode.
        if (err instanceof ApiError && err.code.startsWith('capture_')) {
          setCaptureFailed(err.message);
          setMode('screenshot');
        }
      },
    });
  }

  function reset(): void {
    setResult(null);
    setUrl('');
    setText('');
    setImage(null);
    setFileError(null);
    setCaptureFailed(null);
    setMode('url');
    audit.reset();
  }

  // ── Result view ────────────────────────────────────────────────────────────
  if (result) {
    return (
      <div className="mx-auto max-w-[820px] px-5 py-10 sm:px-8">
        <header className="mb-7 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div>
              <p className="font-mono text-[11px] font-700 uppercase tracking-[0.18em] text-pink">
                {t('audit.resultKicker')}
              </p>
              <h1 className="font-display text-[clamp(20px,3vw,26px)] font-800 tracking-[-0.025em]">
                {t('audit.resultTitle')}
              </h1>
            </div>
          </div>
          <span className="hidden rounded-full bg-emerald-500/10 px-3 py-1.5 text-[12px] font-700 text-emerald-600 sm:inline-flex">
            ✓ {t('audit.addedToPipeline')}
          </span>
        </header>

        <AuditReport analysis={result.analysis} />

        <div className="mt-7 flex flex-wrap gap-3">
          <button onClick={reset} className="btn-primary">
            <UserSearch size={16} /> {t('audit.again')}
          </button>
          <button onClick={() => navigate('/prospects')} className="btn-ghost">
            <ClipboardList size={15} /> {t('audit.viewPipeline')}
          </button>
        </div>
      </div>
    );
  }

  // ── Input view ─────────────────────────────────────────────────────────────
  return (
    <div className="mx-auto max-w-[680px] px-5 py-10 sm:px-8">
      <header className="mb-7 flex items-center gap-3.5">
        <div>
          <p className="font-mono text-[11px] font-700 uppercase tracking-[0.18em] text-pink">
            {t('audit.kicker')}
          </p>
          <h1 className="font-display text-[clamp(22px,3vw,30px)] font-800 tracking-[-0.025em]">
            {t('audit.title')}
          </h1>
        </div>
      </header>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className="card-hi rounded-[26px] p-6 sm:p-8"
      >
        {/* Mode selector */}
        <div className="mb-5 flex gap-1.5 rounded-2xl bg-ink/[0.04] p-1">
          {MODES.map((m) => {
            const Icon = m.icon;
            const active = mode === m.id;
            return (
              <button
                key={m.id}
                onClick={() => setMode(m.id)}
                className="relative flex flex-1 items-center justify-center gap-1.5 rounded-xl px-2 py-2 text-[12.5px] font-700 transition"
              >
                {active && (
                  <motion.span
                    layoutId="audit-mode"
                    className="absolute inset-0 rounded-xl bg-white shadow-[0_4px_12px_-4px_rgba(10,18,38,0.2)]"
                    transition={{ type: 'spring', damping: 28, stiffness: 320 }}
                  />
                )}
                <span className={`relative flex items-center gap-1.5 ${active ? 'text-ink' : 'text-ink/50'}`}>
                  <Icon size={14} />
                  <span className="hidden sm:inline">{t(m.labelKey)}</span>
                </span>
              </button>
            );
          })}
        </div>

        {/* Capture-failed fallback banner */}
        <AnimatePresence>
          {captureFailed && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-4 overflow-hidden"
            >
              <div className="rounded-2xl border border-amber-300/60 bg-amber-500/10 p-4">
                <div className="flex items-start gap-2.5">
                  <AlertCircle size={18} className="mt-0.5 shrink-0 text-amber-600" />
                  <div className="flex-1">
                    <p className="font-display text-[13.5px] font-800 text-amber-900">
                      {t('audit.errorTitle')}
                    </p>
                    <p className="mt-0.5 text-[13px] leading-[1.5] text-amber-800/85">
                      {captureFailed}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        onClick={() => {
                          setCaptureFailed(null);
                          setMode('screenshot');
                        }}
                        className="inline-flex items-center gap-1.5 rounded-xl bg-amber-600 px-3 py-1.5 text-[12.5px] font-700 text-white transition hover:bg-amber-700"
                      >
                        <Camera size={13} />
                        {t('audit.pasteScreenshot')}
                      </button>
                      <button
                        onClick={() => {
                          setCaptureFailed(null);
                          setMode('text');
                        }}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-amber-600/40 bg-white/70 px-3 py-1.5 text-[12.5px] font-700 text-amber-900 transition hover:bg-white"
                      >
                        <Type size={13} />
                        Coller le profil en texte
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── URL mode ── */}
        {mode === 'url' && (
          <div>
            <p className="mb-3 text-[14px] leading-[1.6] text-ink/65">
              {t('audit.intro')}
            </p>
            <div className="relative">
              <Link2
                size={16}
                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-ink/35"
              />
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && runAudit()}
                placeholder={t('audit.urlPlaceholder') as string}
                className="w-full rounded-2xl border border-ink/[0.12] bg-white py-3 pl-10 pr-3.5
                           text-[14px] text-ink outline-none transition
                           focus:border-pink/60 focus:ring-2 focus:ring-pink/20"
              />
            </div>
            {url.trim() !== '' && !isUrlValid && (
              <p className="mt-1.5 text-[12px] font-600 text-ink/50">
                Lien non reconnu — utilisez une URL instagram.com, tiktok.com ou linkedin.com.
              </p>
            )}
          </div>
        )}

        {/* ── Screenshot mode ── */}
        {mode === 'screenshot' && (
          <div>
            <p className="mb-3 text-[14px] leading-[1.6] text-ink/65">
              {t('audit.screenshotIntro')}
            </p>
            <Dropzone image={image} onFile={handleFile} onClear={() => setImage(null)} />
            {fileError && <p className="mt-2 text-[12.5px] font-600 text-pink-deep">{fileError}</p>}
            <div className="mt-3">
              <label className="mb-1.5 block font-mono text-[10px] font-700 uppercase tracking-[0.14em] text-ink/45">
                {t('audit.screenshotMoreContext')}
              </label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={2}
                maxLength={8000}
                placeholder={t("audit.screenshotMoreContextPlaceholder")}
                className="w-full resize-none rounded-2xl border border-ink/[0.12] bg-white px-4 py-2.5
                           text-[13.5px] leading-[1.5] text-ink outline-none transition
                           focus:border-pink/60 focus:ring-2 focus:ring-pink/20"
              />
            </div>
          </div>
        )}

        {/* ── Text mode ── */}
        {mode === 'text' && (
          <div>
            <p className="mb-3 text-[14px] leading-[1.6] text-ink/65">
              {t('audit.textIntro')}
            </p>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={6}
              maxLength={8000}
              placeholder={t("audit.textPlaceholderLong")}
              className="w-full resize-none rounded-2xl border border-ink/[0.12] bg-white px-4 py-3
                         text-[14px] leading-[1.55] text-ink outline-none transition
                         focus:border-pink/60 focus:ring-2 focus:ring-pink/20"
            />
          </div>
        )}

        {audit.isError && isQuotaError(audit.error) && <QuotaReachedNotice className="mt-3" />}

        {audit.isError && !captureFailed && !isQuotaError(audit.error) && (
          <p className="mt-3 rounded-xl bg-pink/10 px-3.5 py-2.5 text-[13px] font-600 text-pink-deep">
            {audit.error instanceof ApiError
              ? audit.error.message
              : t('audit.errorGeneric')}
          </p>
        )}

        <button
          onClick={runAudit}
          disabled={!canSubmit}
          className="btn-primary mt-6 w-full justify-center disabled:opacity-50"
        >
          {audit.isPending ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              {t('audit.analyzing')}
            </>
          ) : (
            <>
              <Sparkles size={16} /> {t('audit.launch')}
            </>
          )}
        </button>
      </motion.div>

      <button
        onClick={() => navigate('/prospects')}
        className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-600 text-ink/55 transition hover:text-pink"
      >
        <ClipboardList size={14} /> {t('audit.viewPipeline')}
        <ArrowRight size={13} />
      </button>
    </div>
  );
}

function Dropzone({
  image,
  onFile,
  onClear,
}: {
  image: { preview: string } | null;
  onFile: (f: File) => void;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  const [dragging, setDragging] = useState(false);

  if (image) {
    return (
      <div className="relative overflow-hidden rounded-2xl border border-ink/[0.1]">
        <img
          src={image.preview}
          alt={t("audit.screenshotAlt")}
          className="max-h-72 w-full bg-ink/[0.03] object-contain"
        />
        <button
          onClick={onClear}
          className="absolute right-2.5 top-2.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-night/80 text-white backdrop-blur transition hover:bg-night"
          aria-label={t('audit.removeImage')}
        >
          <X size={15} />
        </button>
      </div>
    );
  }

  return (
    <label
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        const file = e.dataTransfer.files[0];
        if (file) onFile(file);
      }}
      className={`flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed
                  px-6 py-9 text-center transition ${
                    dragging
                      ? 'border-pink/60 bg-pink/[0.04]'
                      : 'border-ink/[0.14] hover:border-pink/40 hover:bg-ink/[0.02]'
                  }`}
    >
      <span className="mb-2.5 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-blue to-violet text-white">
        <ImagePlus size={22} />
      </span>
      <span className="font-display text-[14.5px] font-700 text-ink">
        {t('audit.screenshotHint')}
      </span>
      <span className="mt-0.5 text-[12.5px] text-ink/55">
        {t('audit.dropzoneHint')}
      </span>
      <input
        type="file"
        accept={ACCEPTED.join(',')}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onFile(file);
        }}
      />
    </label>
  );
}
